import { useNavigate } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { PageHeader } from "../components/AppLayout";
import { Clock, ArrowRight } from "lucide-react";

const PASOS = [
  {
    min: "0:00 – 0:40",
    titulo: "Arrancá con el Panel del dueño",
    ruta: "/",
    guion:
      "Mostrá la foto del negocio en un vistazo: cuántas consultas se respondieron fuera de horario esta semana, visitas, cobros por cobrar, reclamos y contratos que vencen o ajustan. Remarcá que todo esto pasó sin que nadie estuviera en la oficina.",
  },
  {
    min: "0:40 – 1:40",
    titulo: "El Asistente 24/7 en acción",
    ruta: "/asistente",
    guion:
      "Abrí una conversación con el badge 'Respondida fuera de horario'. Explicá que la oficina cierra a las 18 y el bot siguió atendiendo. Bajá al simulador, elegí una propiedad y escribí 'quiero visitarlo' a las 21:30: el bot responde con la ficha y ofrece agendar. Después tocá 'Tomar conversación' para mostrar el pase a un agente.",
  },
  {
    min: "1:40 – 2:30",
    titulo: "Coordinación de visitas",
    ruta: "/visitas",
    guion:
      "Mostrá un depto ocupado: el inquilino marcó franjas, el interesado elige una y se confirma a los tres (interesado, inquilino, agente). Señalá los estados (propuesta → confirmada) y que salen recordatorios automáticos.",
  },
  {
    min: "2:30 – 3:00",
    titulo: "Agenda semanal",
    ruta: "/agenda",
    guion:
      "Filtrá por agente y arrastrá una visita a otro horario para mostrar que mover algo es inmediato. Visitas, firmas, vencimientos y tareas, todo en una sola vista.",
  },
  {
    min: "3:00 – 4:00",
    titulo: "Administración de alquileres",
    ruta: "/alquileres",
    guion:
      "Mostrá el buscador y los filtros en la tabla de contratos (funciona igual con 5 o 500). Pasá a 'Cobros' y mandá un recordatorio por WhatsApp a un atrasado. En 'Ajustes', mostrá el aviso del nuevo monto por ICL. Cerrá descargando una Liquidación en PDF real.",
  },
  {
    min: "4:00 – 4:30",
    titulo: "Portal y propiedades",
    ruta: "/portales",
    guion:
      "Cambiá entre vista inquilino y propietario: cada uno ve lo suyo. Saltá a Propiedades, abrí una ficha y usá el comparador lado a lado (precio por m², expensas, amenities) para mostrar cómo el comprador se saca las dudas.",
  },
  {
    min: "4:30 – 5:00",
    titulo: "El cierre que vende: 'Se adapta a tu inmobiliaria'",
    ruta: "/configuracion",
    guion:
      "Entrá a Configuración, cambiá el nombre a la inmobiliaria del cliente y elegí su color principal. Toda la app se recolorea al instante. Rematá: 'Esto que estás viendo ya es tu inmobiliaria. Y los datos se conectan a tu base sin rehacer nada.'",
  },
];

export default function RecorridoDemo() {
  const navigate = useNavigate();
  return (
    <div>
      <PageHeader
        titulo="Recorrido de demo — 5 minutos"
        descripcion="Guion paso a paso para mostrar la demo en una reunión de venta. Cada paso tiene su minutaje, la pantalla y qué decir."
      />
      <div className="space-y-4">
        {PASOS.map((p, i) => (
          <Card key={i} className="rounded-lg border-slate-200 shadow-sm overflow-hidden">
            <div className="flex flex-col sm:flex-row">
              <div className="sm:w-48 shrink-0 bg-brand-light px-5 py-4 flex sm:flex-col items-center sm:items-start gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-md bg-brand text-white text-sm font-bold">{i + 1}</div>
                <span className="flex items-center gap-1 text-xs font-medium text-brand"><Clock className="h-3.5 w-3.5" /> {p.min}</span>
              </div>
              <div className="flex-1 px-5 py-4">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-base font-semibold text-slate-900">{p.titulo}</h3>
                  <Button size="sm" variant="outline" className="rounded-md shrink-0" data-testid={`demo-goto-${i}`} onClick={() => navigate(p.ruta)}>
                    Ir <ArrowRight className="ml-1 h-3.5 w-3.5" />
                  </Button>
                </div>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.guion}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>
      <Card className="mt-6 p-5 rounded-lg border-slate-200 shadow-sm bg-slate-50">
        <p className="text-sm text-slate-600">
          <span className="font-semibold text-slate-800">Tip de cierre:</span> dejá la pantalla en Configuración con el nombre y color del cliente. El impacto de ver "su" inmobiliaria funcionando es el mejor remate. Este recorrido también está en <code className="text-xs">/docs/recorrido-demo.md</code>.
        </p>
      </Card>
    </div>
  );
}
